<script lang="ts" setup>
import type { AuthTabEnum } from '~/enums'
import {
  IGNORE_FOOTER_MOBILE_URL_NAME,
  IGNORE_BOTTOM_MOBILE_URL_NAME,
} from '~/configs'
import { FULLSCREEN_MOBILE_URL } from '~/constants'
import { useTimeOnPage } from '~/composables/tracking-events/use-time-on-page'
import { useCasinoProvider } from '~/composables/game/use-casino-provider'
import { useFilterGame } from '~/composables/game/use-filter'
import { useModalsStore } from '#lib/stores'

useHead({
  meta: [
    {
      name: 'viewport',
      content:
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no',
    },
  ],
})
const $isLighthouseBot = useNuxtApp().$isLighthouseBot as boolean
const { EVENT_ENABLE } = useRuntimeConfig().public
const modalStore = useModalsStore()
const { isOpenAuthentication } = storeToRefs(modalStore)
const setOpenAuthModal = modalStore.setOpenAuthModal
const route = useRoute()
const { isLogged } = useUser()
const { isLobbyGameFilter, isLiveCasinoFilter } = useFilterGame()
const { fetchFavoriteGamesByUser } = useFavoriteGames()
const { casinoProviders, setCasinoProvider } = useCasinoProvider()
const { fetchCasinoProviders } = useCasino({
  gameLimit: 0,
})
const { isIos } = useDevice()
const authStore = useAuthStore()

useSyncAuthenticateDataTab()
useTimeOnPage()

onMounted(() => {
  setTimeout(async () => {
    if (!casinoProviders.value?.length) {
      const data = await fetchCasinoProviders({})
      if (data?.length) {
        setCasinoProvider(data.filter((item) => item.count > 0))
      }
    }
  })
  fetchFavoriteGamesByUser()
})

watch(
  () => isLogged.value,
  () => {
    fetchFavoriteGamesByUser()
  },
)

const isShowFooter = (): boolean => {
  return !IGNORE_FOOTER_MOBILE_URL_NAME.includes(route.path.split('/')?.[1])
}

const isFullscreen = (): boolean => {
  return (
    (FULLSCREEN_MOBILE_URL as string[]).includes(route.path) ||
    route.path.includes('.html')
  )
}

const isShowMenuBottom = (): boolean => {
  return !IGNORE_BOTTOM_MOBILE_URL_NAME.includes(route.path.split('/')?.[1])
}

const onAuthTabChange = (tab: AuthTabEnum) => {
  authStore.setSelectedAuthTab(tab)
}

const isOpenAuthenticationInterceptor = computed({
  get: () => isOpenAuthentication.value,
  set: (value) => {
    if (!value && authStore.enablePreEscape) {
      authStore.setWarningEscaped(true)
      return
    }
    setOpenAuthModal(value)
  },
})
</script>

<template>
  <main
    :class="[
      'layout-mobile',
      { 'layout-mobile--ios': isIos },
      { 'layout-mobile--full-screen': isFullscreen() },
      { 'layout-mobile--live-casino': isLiveCasinoFilter },
      { 'layout-mobile--lobby': isLobbyGameFilter },
    ]"
  >
    <MobileHeader v-if="!isFullscreen()" />
    <MobilePagesLobbyGameFilter v-if="isLobbyGameFilter" />
    <MobilePagesLiveCasinoFilter v-if="isLiveCasinoFilter" />
    <slot />
    <MobileFooter v-if="isShowFooter() && !isFullscreen()" />
    <MobileCommonBottomNavigationBar
      v-if="isShowMenuBottom() && !isFullscreen() && !authStore.isSidebarOpen"
    />
    <ClientOnly>
      <ModalEventWelcome v-if="EVENT_ENABLE && !$isLighthouseBot"/>
      <CommonToastContainer expired-class="mobile-expired-confirmed" />
      <ModalAuthentication
        v-model="isOpenAuthenticationInterceptor"
        custom-class="mobile-authentication"
        :selected-tab="authStore.selectedAuthTab"
        :on-change-tab="onAuthTabChange"
      />
      <ModalForgotPasswordSendMail />
      <ModalForgotPasswordSendReset />
      <DesktopCommonContact />
      <EventDrawingLotsCommonFloating />
    </ClientOnly>
  </main>
</template>

<style lang="scss">
@import '~/assets/styles/components/layouts/mobile.scss';
</style>
